import { extractUnitOptions } from '#/modules/imsMgmt/helper/materialUnit/extractUnitOptions';
import { pickDefaultUnitId } from '#/modules/imsMgmt/helper/materialUnit/pickDefaultUnitId';
import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import { resolveUnitToBaseRate } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitToBaseRate';
import { maxIssueQtyInUnit } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueQtyBounds';

/** 可用量（base）÷ rate(unit) 的可輸入整數上限；缺 rate 當 1。 */
export { maxIssueQtyInUnit as maxTransferQtyInUnit };

/**
 * 表單值 → create／update body（寫入形狀）。
 * Header 不送 sourceType／sourceId（後端自填 MANUAL／null）；可送 issueReason。
 * Line：invStockId＋正整數 qty＋可選 unitId＋toLocationId＋stockNoPolicy＋remark。
 */
export function buildStockTransferWritePayload(values, { updatedAt = null } = {}) {
  const payload = {
    transferDate: values.transferDate || null,
    toWarehouseId: values.toWarehouseId || null,
    issueReason: values.issueReason || null,
    remark: values.remark || null,
    lines: (values.lines ?? []).map((line) => {
      const linePayload = {
        invStockId: line.invStockId,
        qty: line.qty === '' || line.qty == null ? null : Number(line.qty),
        toLocationId: line.toLocationId || null,
        stockNoPolicy: line.stockNoPolicy || 'TRANSFERRED_KEEP_OLD',
        remark: line.remark || null,
      };

      const unitId = String(line.unitId ?? '').trim();
      if (unitId) {
        linePayload.unitId = unitId;
      }

      return linePayload;
    }),
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

function formatWarehouseLabel(warehouse) {
  if (!warehouse) {
    return '';
  }
  return [warehouse.code, warehouse.name].filter(Boolean).join(' ') || warehouse.id || '';
}

function formatLocationLabel(location) {
  if (!location) {
    return '';
  }
  return [location.code, location.name].filter(Boolean).join(' ') || location.id || '';
}

function resolveSourceAvailableBase(line) {
  const source = line?.sourceInvStock ?? line?.invStock ?? null;
  const availableQty = Number(
    source?.availableQty ?? source?.baseQty ?? source?.systemQty,
  );
  return Number.isFinite(availableQty) && availableQty > 0 ? availableQty : 0;
}

/**
 * detail → 表單 defaultValues。
 * 回填 qty／unit.id（不自行換算）；availableQty＝庫存 base（勿用 line.qty）。
 */
export function mapStockTransferDetailToFormValues(detail, { includeSourceType = true } = {}) {
  const lines = detail?.lines ?? [];

  const values = {
    transferDate: detail?.transferDate ?? '',
    toWarehouseId: detail?.toWarehouse?.id ?? detail?.toWarehouseId ?? '',
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: lines.map((line) => {
      const mtr = line?.mtr ?? null;
      const unitOptions = extractUnitOptions(mtr ?? line);
      const source = line.sourceInvStock ?? line.invStock;

      return {
        invStockId: source?.id ?? line.invStockId ?? '',
        stockNo: source?.stockNo ?? line.stockNo ?? '',
        mtrId: mtr?.id ?? source?.mtr?.id ?? '',
        mtrNo: mtr?.mtrNo ?? '',
        mtrName: mtr?.name ?? '',
        fromWarehouseId: line.fromWarehouse?.id ?? '',
        fromWarehouseLabel: formatWarehouseLabel(line.fromWarehouse),
        fromLocationLabel: formatLocationLabel(line.fromWarehouse?.location),
        availableQty: resolveSourceAvailableBase(line),
        unitId: resolveUnitId(line.unit) || resolveUnitId(line),
        unitOptions,
        qty: line.qty ?? '',
        toLocationId: line.toLocation?.id ?? line.toLocationId ?? '',
        stockNoPolicy: line.stockNoPolicy ?? 'TRANSFERRED_KEEP_OLD',
        remark: line.remark ?? '',
      };
    }),
  };

  if (includeSourceType) {
    values.sourceType = detail?.sourceType ?? 'MANUAL';
    values.sourceId = detail?.sourceId ?? '';
  }

  return values;
}

export function createEmptyTransferLine() {
  return {
    invStockId: '',
    stockNo: '',
    mtrId: '',
    mtrNo: '',
    mtrName: '',
    fromWarehouseId: '',
    fromWarehouseLabel: '',
    fromLocationLabel: '',
    availableQty: 0,
    unitId: '',
    unitOptions: [],
    qty: '',
    toLocationId: '',
    stockNoPolicy: 'TRANSFERRED_KEEP_OLD',
    remark: '',
  };
}

/**
 * 庫存列表列 → 轉倉明細列（新建預設全轉＋TRANSFERRED_KEEP_OLD）。
 * unitId 先空，options 就緒後由 UI 套 base；預設 qty＝floor(availableBase / rate(defaultUnit))。
 */
export function mapInvStockRowToTransferLine(row) {
  if (!row?.id) {
    return null;
  }

  const availableQty = getInvStockAvailableQty(row);
  const warehouse = row.warehouse;
  const unitOptions = extractUnitOptions(row.mtr ?? row);
  const defaultUnitId = pickDefaultUnitId(unitOptions);
  const maxQty = maxIssueQtyInUnit(availableQty, unitOptions, defaultUnitId || '');
  const qty = Math.max(1, maxQty > 0 ? maxQty : 1);

  return {
    invStockId: row.id,
    stockNo: row.stockNo ?? '',
    mtrId: row.mtr?.id ?? '',
    mtrNo: row.mtr?.mtrNo ?? '',
    mtrName: row.mtr?.name ?? '',
    fromWarehouseId: warehouse?.id ?? '',
    fromWarehouseLabel: formatWarehouseLabel(warehouse),
    fromLocationLabel: formatLocationLabel(warehouse?.location),
    availableQty,
    unitId: '',
    unitOptions,
    qty,
    toLocationId: '',
    stockNoPolicy: 'TRANSFERRED_KEEP_OLD',
    remark: '',
  };
}

function readInventoryStockBaseQty(row) {
  const nested = row?.qty;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return undefined;
  }
  if (nested.baseQty == null || nested.baseQty === '') {
    return undefined;
  }
  return nested.baseQty;
}

export function getInvStockAvailableQty(row) {
  const raw = readInventoryStockBaseQty(row)
    ?? row?.availableQty
    ?? row?.systemQty
    ?? row?.baseQty;
  const qty = Number(raw);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

/**
 * lookup／庫存列的 qty → 單位選項。
 * 1 個進位單位＝convertUnit.convertRate 個基本單位；基本單位為 1。
 * 空的 convertUnit（沒有 id、名稱）略過。
 *
 * @param {object|null|undefined} qty
 * @returns {object[]}
 */
export function unitOptionsFromInventoryQty(qty) {
  if (!qty || typeof qty !== 'object' || Array.isArray(qty)) {
    return [];
  }

  const options = [];
  const base = qty.baseUnit;
  if (base && typeof base === 'object' && (base.id || base.name)) {
    options.push({
      id: base.id ?? '',
      code: base.code ?? '',
      name: base.name ?? '',
      baseUnit: true,
    });
  }

  const convert = qty.convertUnit;
  if (!convert || typeof convert !== 'object') {
    return options;
  }
  const hasIdentity = Boolean(String(convert.id ?? '').trim() || String(convert.name ?? '').trim());
  const convertRate = Number(convert.convertRate);
  if (!hasIdentity || !Number.isFinite(convertRate) || convertRate <= 0) {
    return options;
  }
  options.push({
    id: convert.id ?? '',
    code: convert.code ?? '',
    name: convert.name ?? '',
    baseUnit: false,
    convertRate,
  });
  return options;
}

function mergeInventoryQtyUnits(line, stock) {
  const incoming = unitOptionsFromInventoryQty(stock?.qty);
  let options = Array.isArray(line?.unitOptions) ? [...line.unitOptions] : [];
  incoming.forEach((option) => {
    const id = resolveUnitId(option);
    const index = id
      ? options.findIndex((row) => resolveUnitId(row) === id)
      : -1;
    if (index < 0) {
      options = [...options, option];
      return;
    }
    const current = options[index];
    const next = { ...current, name: option.name || current.name, baseUnit: option.baseUnit };
    if (option.convertRate != null) {
      next.convertRate = option.convertRate;
    }
    options = options.map((row, rowIndex) => (rowIndex === index ? next : row));
  });
  return options;
}

/**
 * 用 lookup 的 qty.baseQty 覆寫明細 availableQty，並把換算率寫進對應單位。
 * 不讀轉倉列的 qty／baseQty。查不到的列把 availableQty 留空。
 *
 * @param {object} formValues
 * @param {Record<string, object|null|undefined>} inventoryById
 */
export function applyLiveInventoryAvailableQty(formValues, inventoryById = {}) {
  const lookup = inventoryById ?? {};
  return {
    ...formValues,
    lines: (formValues.lines ?? []).map((line) => {
      const stockId = String(line?.invStockId ?? '').trim();
      const stock = stockId ? lookup[stockId] : null;
      if (!stock) {
        return { ...line, availableQty: null };
      }
      return {
        ...line,
        availableQty: getInvStockAvailableQty(stock),
        unitOptions: mergeInventoryQtyUnits(line, stock),
      };
    }),
  };
}

/**
 * 全轉：輸入 qty×rate(unit) ≥ availableBase。
 * 未傳 unitOptions／unitId 時 rate＝1（相容工單轉倉 base qty）。
 *
 * @param {unknown} qty
 * @param {unknown} availableBase
 * @param {Array<object>|null|undefined} [unitOptions]
 * @param {string} [unitId]
 */
export function isFullTransferQty(qty, availableBase, unitOptions, unitId) {
  const qtyNum = Number(qty);
  const availableNum = Number(availableBase);
  if (!Number.isFinite(qtyNum) || !Number.isFinite(availableNum) || availableNum <= 0) {
    return false;
  }
  const rate = resolveUnitToBaseRate(unitOptions, unitId) ?? 1;
  return qtyNum * rate >= availableNum;
}

/** @deprecated 請改用 `#/utils/quantityBounds` 的 `clampQtyToAvailable` */
export { clampQtyToAvailable as clampTransferQty } from '#/utils/quantityBounds';

export function buildExcludeWarehouseFilter(warehouseId) {
  if (!warehouseId) {
    return {};
  }
  return { warehouseId: `!=${warehouseId}` };
}

/**
 * 轉倉選庫 warehouseId：未選倉 → !=目標倉；有選 → include（自動排除目標倉，不送 !=）。
 * @param {string|null|undefined} excludeWarehouseId
 * @param {unknown} userWarehouseId
 * @returns {{ warehouseId?: string|string[] }}
 */
export function resolveInvStockPickerWarehouseFilter(
  excludeWarehouseId,
  userWarehouseId,
) {
  const selected = (
    Array.isArray(userWarehouseId)
      ? userWarehouseId
      : (userWarehouseId == null || userWarehouseId === ''
        ? []
        : [userWarehouseId])
  )
    .map((id) => String(id).trim())
    .filter(Boolean);

  const excludeId = excludeWarehouseId != null && excludeWarehouseId !== ''
    ? String(excludeWarehouseId)
    : null;
  const includeIds = excludeId
    ? selected.filter((id) => id !== excludeId)
    : selected;

  if (includeIds.length > 0) {
    return { warehouseId: includeIds };
  }

  return buildExcludeWarehouseFilter(excludeId);
}
